/**
 * useManualItemDefinition — Step 0 Manual mode lifecycle.
 *
 * Owns the markdown editor state, persists to localStorage on every
 * keystroke (for crash recovery — matches legacy
 * `ManualItemDefinitionEditor`), and on submit:
 *
 *   1. `actions.setStepStatus(0, 'generating')`
 *   2. `saveManualItemDefinition(sessionId, markdown)`
 *   3. `startAnalysis('manual-{sessionId}', sessionId, tenantId)`
 *   4. On success: status → approved, advance to Step 1, notify
 *      `onThreadIdResolved` if backend returns a new thread_id.
 *   5. On error: status → failed with detail.
 *
 * Backend interprets the `manual-{sessionId}` pseudo file_id and
 * skips `analysis_agent`'s document-extraction step (it uses the
 * stored markdown directly).
 */
import { useCallback, useEffect, useState } from 'react';
import { saveManualItemDefinition } from '../../../api/sessions';
import { startAnalysis } from '../../../api/documents';
import type { ManualFlowState } from '../step-components/stepBodyProps';
import type { WorkflowActions } from '../state/workflowV2Types';

const UPLOAD_STEP_ID = 0;
const LS_KEY_PREFIX = 'manual_item_def_';

const ISO_TEMPLATE = `# Item Definition

## 1. Overview
Describe the item under analysis, its primary function, and the system context.

## 2. System Boundaries
Define what is in scope and what is out of scope. Identify external interfaces.

## 3. System Architecture
List components, their roles, and how they communicate.

## 4. Operational Environment
Describe physical, network, and use-case environments where the item operates.

## 5. Cybersecurity Properties
Confidentiality, Integrity, Availability requirements. Safety/privacy ties.

## 6. Assumptions & Constraints
Operational, organizational, and regulatory assumptions.

## 7. External Dependencies
Cloud services, third-party integrations, supply chain components.

## 8. Safety Linkage
ISO 26262 ASIL allocations, safety goals, hazards relevant to cybersecurity.

## 9. Compliance & Standards
Applicable regulations (UN R155, ISO 21434, GDPR, etc.).

## 10. References
Supporting documents, diagrams, prior analyses.
`;

export interface UseManualItemDefinitionInput {
  sessionId: string | null | undefined;
  tenantId: string | null | undefined;
  actions: WorkflowActions;
  onThreadIdResolved: (threadId: string) => void;
}

export function useManualItemDefinition({
  sessionId,
  tenantId,
  actions,
  onThreadIdResolved,
}: UseManualItemDefinitionInput): ManualFlowState {
  const storageKey = sessionId ? `${LS_KEY_PREFIX}${sessionId}` : null;

  const [markdown, setMarkdown] = useState<string>(() => {
    if (!storageKey) return ISO_TEMPLATE;
    try {
      return window.localStorage.getItem(storageKey) ?? ISO_TEMPLATE;
    } catch {
      return ISO_TEMPLATE;
    }
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist on every change. Matches the legacy crash-recovery
  // behavior — refresh restores the in-progress draft.
  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, markdown);
    } catch {
      /* quota or private-mode — non-fatal */
    }
  }, [storageKey, markdown]);

  const onChange = useCallback((next: string) => {
    setMarkdown(next);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!sessionId || !tenantId) {
      actions.setStepStatus(UPLOAD_STEP_ID, 'failed', 'Missing session or tenant context.');
      return;
    }
    setError(null);
    setSaving(true);
    actions.setStepStatus(UPLOAD_STEP_ID, 'generating');
    try {
      await saveManualItemDefinition(sessionId, markdown);
      const startResp: any = await startAnalysis(`manual-${sessionId}`, sessionId, tenantId);
      const newThreadId = startResp?.thread_id;
      if (newThreadId) onThreadIdResolved(newThreadId);
      // Clear the localStorage draft once analysis is kicked off —
      // the markdown is now persisted server-side.
      if (storageKey) {
        try { window.localStorage.removeItem(storageKey); } catch { /* noop */ }
      }
      actions.setStepStatus(UPLOAD_STEP_ID, 'approved');
      // Optimistic: Step 1's agent has just started on the backend.
      // Without this write, the rail circle stays at "1" until the
      // first polling tick (~3s later) catches up — the user sees a
      // numbered circle instead of the in-flight spinner. Polling
      // will overwrite this with the real backend status (still
      // `generating`) shortly. Parity with `useUploadFlow.ts:65`.
      actions.setStepStatus(1, 'generating');
      actions.selectStep(1);
    } catch (e: any) {
      const message = e?.response?.data?.detail || e?.message || 'Failed to save manual definition';
      setError(message);
      actions.setStepStatus(UPLOAD_STEP_ID, 'failed', message);
    } finally {
      setSaving(false);
    }
  }, [sessionId, tenantId, actions, markdown, storageKey, onThreadIdResolved]);

  return { markdown, saving, error, onChange, onSubmit };
}
